import { EnumRedirectTo } from "@/types/(my-space)/users.interface";
import { uniqueId } from "lodash";
import toast from "react-hot-toast";

// Pass this id to any hot-toast to prevent creating new toasts on every api call,
// thus updating existing toast only for clutter free toasts
// e.g. toast.success(message, {id: commonToastId})
export const commonToastId = uniqueId();

export const redirectToMap = (destination?: string): string => {
  switch (destination) {
    case EnumRedirectTo.CREATE_COMPANY:
      return "/create-company";
    case EnumRedirectTo.DASHBOARD:
      return "/company";
    case EnumRedirectTo.HOME:
      return "/";
    default:
      return "/";
  }
};

const sanitizeFileName = (name: string) => name.replace(/[/\\?%*:|"<>]/g, "-");

export const downloadFile = async (fileURL: string, fileName?: string, signal?: AbortSignal) => {
  if (!fileURL.startsWith("https://") && !fileURL.startsWith("http://")) {
    console.error("downloadFile: rejected non-http URL");
    return;
  }

  const urlSegment = fileURL.split("/").pop()?.split("?")[0] ?? "Document";
  const safeFallback = sanitizeFileName(fileName || urlSegment);

  try {
    const response = await fetch(fileURL, {
      method: "GET",
      mode: "cors",
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

    const disposition = response.headers.get("Content-Disposition");
    const dispositionName = disposition?.match(/filename="?([^";\n]+)"?/i)?.[1];
    const resolvedName = sanitizeFileName(dispositionName ?? safeFallback);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = resolvedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 100);

    toast.success("File downloaded successfully", { id: commonToastId });
  } catch (err: any) {
    if (err?.name === "AbortError") return;

    // CORS / network failure — open in new tab as fallback
    if (err instanceof TypeError) {
      window.open(fileURL, "_blank");
      toast.success("Opened in new tab — save manually", { id: commonToastId });
      return;
    }

    console.error("Download failed", err);
    toast.error(err?.message ?? "Download failed", { id: commonToastId });
  }
};
